import { Injectable } from '@angular/core';
import { FormGroup, FormControl, AbstractControl } from '@angular/forms';

declare let $: any;
declare let bootbox: any;

@Injectable()
export class FormService {
	// default error message
	// adopted from simple schema
	// https://github.com/aldeed/meteor-simple-schema/blob/master/simple-schema.js
	// override this to provide custom message
	// [label] is the field name
	public errorMessage: any = {
		required: '[label] is required',
		minlength: '[label] must be at least [min] characters',
		maxlength: '[label] cannot exceed [max] characters',
		notMatch: '[label] is not match',
		IP: '[label] must be a valid IPv4 or IPv6 address',
		IPv4: '[label] must be a valid IPv4 address',
		IPv6: '[label] must be a valid IPv6 address',
		email: '[label] must be a valid e-mail address',
		phone: '[label] must be a valid phone number',
		minNumber: '[label] must be at least [min]',
		maxNumber: '[label] cannot exceed [max]'
	};

	// language array
	public languages: string[] = ['en', 'eng', 'cht', 'chs', 'zh-cn', 'zh-tw', 'zh-hk'];

	// for form group object
	protected _form: FormGroup;

	// form default value
	protected _formDefault:any = {};

	// get form group object
	// public getForm = () => this._form;

	// set form group object
	public setForm = (form: FormGroup): void => {
		this._form = form;
	}

	// set form default value, for .reset() when create()
	// public setFormDefault = (value: any): void => {
	// 	this._formDefault = value;
	// }

	// parse [label], split dots and captialize
	// find errorLabel set by user
	// if not found, use control name
	protected _parseLabel(label: (string|number)[]): string {
		let pointer = $('form');
		for(let i=0; i<label.length; i++) {
			if(i == label.length-1) { // last
				pointer = pointer.find(`[formcontrolname="${label[i]}"]`);
			}
			else {
				pointer = pointer.find(`[formgroupname="${label[i]}"]`);
			}
		}
		
		if(pointer && pointer.attr('errorlabel')) {
			return pointer.attr('errorlabel');
		}
		else {
			let _label = label.map(v => {
				return v.toString().replace(/[_.]/g, ' ');
			}).join(' ');

			return _label[0].toUpperCase() + _label.slice(1);
		}
	}

	// errors object
	protected _errors: any = {};

	// prepare error message, designed for input component
	protected _getError = (name: string, control: AbstractControl, parents: (string|number)[] = []): string => {
		for(let e in control.errors) {
			let msg = this.errorMessage[e].slice(0) || '[label] is invalid';
			parents.push(name);
			msg = msg.replace(/\[label\]/g, this._parseLabel(parents));

			if(!isNaN(control.errors[e].requiredLength)) 
				msg = msg.replace(/\[max\]|\[min\]/g, control.errors[e].requiredLength);

			this._errors[name] = msg;
			return msg;
		}
	}

	// validate on blur, designed for input component
	// TODO: recursive check support
	// public validate = (name: string): string | null => {
	// 	let control = this._form.get(name);

	// 	if(control && !control.valid)
	// 		return this._getError(name);
	// 	else
	// 		return null;
	// }

	// get all error messages, designed for formGroup in parent component (if needed)
	public getErrors = (controls, parents: (string|number)[] = []): any => {

		for(let name in controls) {
			let _parents = parents.slice(0);

			if(controls[name] instanceof FormGroup) {
				_parents.push(name);
				this.getErrors(controls[name].controls, _parents);
			}

			if(!controls[name].valid) {
				this._getError(name, controls[name], _parents);
			}
		}
		
		return this._errors;
	}

	// use bootbox prompt to show errors
	public showErrors = (): void => {
		this._errors = {};
		this.getErrors(this._form.controls);
		let html = '';

		for(let i in this._errors) {
			html += `<li>${this._errors[i]}</li>`;
		}

		bootbox.alert({
			title: '<span class="red">Validation Fail</span>',
			message: `<ul>${html}</ul>`
		});
		delete this._errors['password'];
	}

	// validate match value of current field and another field
	// bind the other field to match
	// e.g. validateMatch.bind(null, 'foo')
	public validateMatch = (match: string, control: AbstractControl): any => {
		let parent = control.parent;
		
		// check valid when form is ready
		//if(parent && match) {
		if(parent && match) {
			let check = parent.get(match);
			if(control.value != check.value)
				return { notMatch: true };
		}
		
		return null;
	}

	// validation regex
	// adpoted from simple schema
	// https://github.com/aldeed/meteor-simple-schema/blob/master/simple-schema.js

	// strict IPv4 expression which allows 0-255 per oktett
	protected RX_IPv4 = '(?:(?:[0-1]?\\d{1,2}|2[0-4]\\d|25[0-5])(?:\\.|$)){4}';

	// strict IPv6 expression which allows (and validates) all shortcuts
	protected RX_IPv6 = '(?:(?:[\\dA-Fa-f]{1,4}(?::|$)){8}' // full adress
	  + '|(?=(?:[^:\\s]|:[^:\\s])*::(?:[^:\\s]|:[^:\\s])*$)' // or min/max one '::'
	  + '[\\dA-Fa-f]{0,4}(?:::?(?:[\\dA-Fa-f]{1,4}|$)){1,6})'; // and short adress

	// regex object
	protected RegEx = {
		Email: /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/,
		Phone: /^[0-9０-９٠-٩۰-۹]{2}$|^[+＋]*(?:[-x‐-―−ー－-／  ­​⁠　()（）［］.\[\]/~⁓∼～*]*[0-9０-９٠-٩۰-۹]){3,}[-x‐-―−ー－-／  ­​⁠　()（）［］.\[\]/~⁓∼～0-9０-９٠-٩۰-۹]*(?:;ext=([0-9０-９٠-٩۰-۹]{1,7})|[  \t,]*(?:e?xt(?:ensi(?:ó?|ó))?n?|ｅ?ｘｔｎ?|[,xｘ#＃~～]|int|anexo|ｉｎｔ)[:\.．]?[  \t,-]*([0-9０-９٠-٩۰-۹]{1,7})#?|[- ]+([0-9０-９٠-٩۰-۹]{1,5})#)?$/i,
		IP: new RegExp('^(?:' + this.RX_IPv4 + '|' + this.RX_IPv6 + ')$'),
		IPv4: new RegExp('^' + this.RX_IPv4 + '$'),
		IPv6: new RegExp('^' + this.RX_IPv6 + '$'),
	}

	// validate ip (allow ipv4 and ipv6)
	public validateIP = (control: AbstractControl): any => {
		if(!control.value || this.RegEx.IP.test(control.value))
			return null;
		else
			return { IP: true };
	}

	// validate ipv4
	public validateIPv4 = (control: AbstractControl): any => {
		if(!control.value || this.RegEx.IPv4.test(control.value))
			return null;
		else
			return { IPv4: true };
	}

	// validate ipv6
	public validateIPv6 = (control: AbstractControl): any => {
		if(!control.value || this.RegEx.IPv6.test(control.value))
			return null;
		else
			return { IPv6: true };
	}

	// validate ip (allow ipv4 and ipv6)
	public validatePhone = (control: AbstractControl): any => {
		if(!control.value || this.RegEx.Phone.test(control.value))
			return null;
		else
			return { phone: true };
	}

	// validate ip (allow ipv4 and ipv6)
	public validateEmail = (control: AbstractControl): any => {
		if(!control.value || this.RegEx.Email.test(control.value))
			return null;
		else
			return { email: true };
	}

	// validate number min
	public validateNumberMin = (length: number): any => {
		return function(control: AbstractControl): any {
			if((!control.value || control.value >= length))
				return null;
			else
				return { minNumber: { actualLength: control.value, requiredLength: length } };
		}
	}

	// validate number max
	public validateNumberMax = (length: number): any => {
		return function(control: AbstractControl): any {
			if((!control.value || control.value <= length))
				return null;
			else
				return { maxNumber: { actualLength: control.value, requiredLength: length } };
		}
	}
}


// WEBPACK FOOTER //
// ./src/services/form.service.ts